import { useOutletContext } from 'react-router-dom'
import { Catalog, PemdReport } from '../../../models/catalogs.models'
import { Box, Button, Grid, useMediaQuery, useTheme } from '@mui/material'
import LoaderOverlay from '../../../components/layout/LoaderOverlay.layout'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useOwnSnackbar from '../../../hooks/useSnackbar.hooks'
import { catalogsService } from '../../../store/catalogs'
import { PDFUtils } from '../../../utils/commons.utils'
import {
  Delete as DeleteIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  EnergySavingsLeaf as EnergySavingsLeafIcon,
} from '@mui/icons-material'
import { traduceCategory } from '../../../models/categories.models'
import NumberGraph from '../../../components/common/graph/Number.graph'
import PieGraph from '../../../components/common/graph/Pie.graph'
import MissingUnitWeightInfo from '../../../components/report/MissingUnitWeightInfo'

const CatalogReportTab = () => {
  const { catalog } = useOutletContext<{
    catalog: Catalog
  }>()
  const { t } = useTranslation()
  const show = useOwnSnackbar()
  const theme = useTheme()
  const onlyXs = useMediaQuery(theme.breakpoints.only('xs'))
  const [loading, setLoading] = useState(false)
  const [pemdReport, setPemdReport] = useState<PemdReport>()
  const reportsContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const getData = async () => {
      setLoading(true)
      try {
        const data = await catalogsService.getPemdReport(catalog._id)
        setPemdReport(data)
      } catch (err: any) {
        show(err)
      }
      setLoading(false)
    }
    getData()
  }, [show, catalog._id])
  const exportPdf = useCallback(async () => {
    setLoading(true)
    try {
      if (!reportsContainerRef.current) throw new Error('No container to export')
      const response = await PDFUtils.captureContainerAsImage(reportsContainerRef.current)
      if (!response) throw new Error('No screenshot to export')
      const { imgData, ratio } = response
      PDFUtils.convertToPdf(imgData, ratio, 'reports')
    } catch (err: any) {
      show(err)
    }
    setLoading(false)
  }, [show])

  return (
    <Box display="flex" flexGrow="1">
      {loading && <LoaderOverlay />}

      <Box
        padding={`15px ${onlyXs ? '10px' : '15px'}`}
        width="100%"
        display="flex"
        position="relative"
        flexDirection="column"
        flexGrow="1">
        {pemdReport && (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={exportPdf}
              sx={{ marginLeft: 'auto', marginBottom: '5px' }}>
              {t('reports:actions.exportPdf')}
            </Button>

            {catalog && pemdReport?.missingUnitWeight && <MissingUnitWeightInfo type="pemd" />}

            <Grid container spacing={3} ref={reportsContainerRef} padding="16px">
              <Grid item xs={12} md={6}>
                <PieGraph
                  title={t('reports:attributes.pemd.waste')}
                  icon={<DeleteIcon />}
                  value={pemdReport.waste.total || 0}
                  subtitle={t('reports:attributes.pemd.tonnage')}
                  unit={t('reports:attributes.tonneUnit')}
                  data={pemdReport.waste.perTypes.map((perType) => ({
                    name: traduceCategory('waste', perType.type as number),
                    value: Number(perType.count.toFixed(2)),
                  }))}
                  showPercent
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PieGraph
                  title={t('reports:attributes.pemd.resource')}
                  icon={<EnergySavingsLeafIcon />}
                  value={pemdReport.resource.total || 0}
                  subtitle={t('reports:attributes.pemd.tonnage')}
                  unit={t('reports:attributes.tonneUnit')}
                  data={pemdReport.resource.perTypes.map((perType) => ({
                    name: traduceCategory('resource', perType.type as number),
                    value: Number(perType.count.toFixed(2)),
                  }))}
                  showPercent
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PieGraph
                  title={t('reports:attributes.pemd.valuation')}
                  icon={<DeleteOutlineOutlinedIcon />}
                  data={pemdReport.valuation.perTypes.map((perType) => ({
                    name: t(`materials:attributes.cerfaWaste.${perType.type}` as any),
                    value: Number(perType.count.toFixed(2)),
                  }))}
                  showPercent
                  unit={t('reports:attributes.tonneUnit')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <NumberGraph
                      title={t('reports:attributes.pemd.resourceCount')}
                      icon={<EnergySavingsLeafIcon />}
                      value={pemdReport.resourceCount}
                      subtitle={t('reports:attributes.pemd.materials')}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <NumberGraph
                      title={t('reports:attributes.pemd.pemPercent')}
                      icon={<EnergySavingsLeafIcon />}
                      value={
                        !pemdReport.waste.total
                          ? ''
                          : ((pemdReport.resource.total / pemdReport.waste.total) * 100).toFixed(2)
                      }
                      subtitle={t('reports:attributes.pemd.percent')}
                    />
                  </Grid>

                  {pemdReport.surfaceWaste && (
                    <Grid item xs={6}>
                      <NumberGraph
                        title={t('reports:attributes.pemd.surfaceWaste')}
                        icon={<DeleteIcon />}
                        value={pemdReport.surfaceWaste.toFixed(2)}
                        subtitle={t('reports:attributes.pemd.surfaceTonnage')}
                      />
                    </Grid>
                  )}
                  {pemdReport.surfaceResource && (
                    <Grid item xs={6}>
                      <NumberGraph
                        title={t('reports:attributes.pemd.surfaceResource')}
                        icon={<EnergySavingsLeafIcon />}
                        value={pemdReport.surfaceResource.toFixed(2)}
                        subtitle={t('reports:attributes.pemd.surfaceTonnage')}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Box>
  )
}
export default CatalogReportTab
